// Goldtanne: "+" bei den Warenkorb-Vorschlägen. Nutzt denselben Weg wie das Produktformular des Themes
// (CartLinesUpdateEvent mit gerenderten Abschnitten), damit Warenkorb, Zähler und Versandbalken sofort stimmen.
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

function cartSectionIds() {
  return [
    ...new Set(
      Array.from(document.querySelectorAll('cart-items-component[data-section-id]')).map(
        (element) => /** @type {HTMLElement} */ (element).dataset.sectionId
      )
    ),
  ];
}

/**
 * Schickt eine Warenkorb-Änderung ab und lässt das Theme Warenkorb, Zähler und Versandbalken neu zeichnen.
 * @param {HTMLElement} source
 * @param {'add'|'update'} action
 * @param {string} url
 * @param {object} body
 * @param {{merchandiseId: string, quantity: number}[]} lines
 * @param {string} name
 */
async function applyCart(source, action, url, body, lines, name) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: cartSectionIds().join(','), sections_url: window.location.pathname }),
    });
    const data = await response.json();
    const cart = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((res) => res.json());

    if (data.status) {
      document.dispatchEvent(new CartErrorEvent({ error: data.message || 'Cart update failed', code: 'INVALID' }));
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: name,
        itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
        sections: data.status ? undefined : data.sections,
        didError: Boolean(data.status),
      },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-gt-add]') : null;
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  event.preventDefault();
  button.disabled = true;
  button.classList.add('is-loading');

  const variantId = String(button.dataset.gtAdd);
  try {
    await applyCart(
      button,
      'add',
      Theme.routes.cart_add_url,
      { items: [{ id: Number(variantId), quantity: 1 }] },
      [{ merchandiseId: variantId, quantity: 1 }],
      'gt-cart-upsell'
    );
  } catch {
    button.disabled = false;
    button.classList.remove('is-loading');
  }
});

// Goldtanne: Mitläufer-Artikel im Warenkorb.
// - Einwegpfand: Produkte mit dem Metafeld custom.pfand ziehen ihren Pfand-Artikel in gleicher Menge mit.
// - Gratis-Beigabe: Jeder Adventskalender zieht eine Packung Lindt Lindor in der gewählten Sorte mit.
// Die Soll- und Ist-Mengen rendert das Snippet gt-cart-progress bei jedem Neuzeichnen des Warenkorbs mit.
const MITLAEUFER = ['script[data-gt-pfand]', 'script[data-gt-gratis]'];
let pfandBusy = false;
let pfandTimer = 0;

async function syncPfand() {
  if (pfandBusy) return;

  /** @type {Record<string, number>} */
  const soll = {};
  /** @type {Record<string, number>} */
  const ist = {};
  /** @type {HTMLElement | null} */
  let source = null;

  for (const selector of MITLAEUFER) {
    const node = document.querySelector(selector);
    if (!node) continue;
    let state;
    try {
      state = JSON.parse(node.textContent || '{}');
    } catch {
      continue;
    }
    source = source || node.parentElement;
    for (const [id, qty] of state.soll || []) soll[id] = (soll[id] || 0) + qty;
    for (const [id, qty] of state.ist || []) ist[id] = (ist[id] || 0) + qty;
  }
  if (!source) return;

  /** @type {Record<string, number>} */
  const updates = {};
  for (const id of new Set([...Object.keys(soll), ...Object.keys(ist)])) {
    if ((soll[id] || 0) !== (ist[id] || 0)) updates[id] = soll[id] || 0;
  }
  if (!Object.keys(updates).length) return;

  pfandBusy = true;
  try {
    await applyCart(
      source,
      'update',
      `${Theme.routes.cart_update_url}.js`,
      { updates },
      Object.entries(updates).map(([id, quantity]) => ({ merchandiseId: id, quantity })),
      'gt-pfand'
    );
  } catch {
    // Beim nächsten Neuzeichnen des Warenkorbs wird erneut abgeglichen.
  } finally {
    pfandBusy = false;
  }
}

function schedulePfand() {
  clearTimeout(pfandTimer);
  pfandTimer = window.setTimeout(syncPfand, 250);
}

new MutationObserver(schedulePfand).observe(document.body, { childList: true, subtree: true, characterData: true });
schedulePfand();
